<template>
  <vue-resizable
    v-on-click-outside="handleClickOutside"
    :class="agentSessionInfo.hasActiveOverlay ? 'z-100' : 'z-101'"
    :drag-selector="dragSelector"
    data-cy="cmd-k-resizable"
    v-bind="cmdKOptions"
    :min-width="activeCmdKWidthAndHeight.minWidth"
    :min-height="activeCmdKWidthAndHeight.minHeight"
    :max-width="activeCmdKWidthAndHeight?.maxWidth"
    :max-height="activeCmdKWidthAndHeight?.maxHeight"
    @mount="updatePosition"
    @resize:end="updatePosition"
    @drag:end="handleDragEnd"
    @drag:start="handleDragStart"
  >
    <div
      ref="draggable"
      class="flex h-full w-full flex-col rounded border shadow-xl transition-colors duration-200"
      :class="[
        fileDragInfo.isDragOver
          ? 'border-blue-400 bg-blue-50'
          : 'border-gray-200 bg-white',
      ]"
      @dragenter="handleFileDragEnter"
      @dragover="handleFileDragOver"
      @dragleave="handleFileDragLeave"
      @drop="handleFileDrop"
    >
      <div class="flex h-full w-full flex-col" data-cy="cmd-k-modal">
        <div
          v-if="fileDragInfo.isDragOver"
          class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded bg-white/70 backdrop-blur-[6px]"
        >
          <div
            class="mb-4 w-18 h-18 flex items-center justify-center rounded-full bg-surface-user-blue"
          >
            <icon-base :icon="IconUploadDoc" width="30" height="30" />
          </div>
          <div class="text-center whitespace-pre-line max-w-[380px]">
            <div class="text-headline font-semibold">
              {{ t("AI_AGENTS.FILE_UPLOAD.DRAG_DROP_TITLE") }}
            </div>
            <div class="text-label">
              {{
                t("AI_AGENTS.FILE_UPLOAD.DRAG_DROP_MESSAGE", {
                  allowedTypes: allowedDocTypes.join(", "),
                })
              }}
            </div>
          </div>
        </div>
        <slot name="header">
          <div class="draggable">
            <div v-if="showHeader">
              <cmd-k-header
                :type="activeType"
                @modal:close="closeModal"
                @navigate:back="handleNavigateBack"
              />
            </div>
          </div>
          <div v-if="!showHeader" class="draggable py-4">
            <command-input
              ref="searchInputRef"
              :placeholder="placeholder"
              :type="activeType"
              :file-uploading-disabled="agentSessionInfo.requestIsLoading"
              @command:update="updateAiSearchPhrase"
              @command:submit="manualSubmit"
              @agent:remove="handleAgentRemove"
            />
          </div>
        </slot>
        <slot name="content">
          <div class="draggable flex flex-1 flex-col overflow-hidden">
            <ai-results
              :type="activeType"
              @modal:close="closeModal"
              @agent:selected="handleAgentSelected"
              @deal:selected="handleDealSelected"
              @session:selected="handleSessionSelected"
              @agent-history:get="handleAgentHistory"
            />
          </div>
        </slot>
      </div>
    </div>
  </vue-resizable>
</template>

<script setup lang="ts">
import { useMagicKeys, watchDebounced, whenever } from "@vueuse/core";
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import VueResizable from "vue-resizable";

import throttle from "lodash/throttle";
import { storeToRefs } from "pinia";

import { AgentFeatureType, AiDialogType } from "@/enums/ai";
import { AnalyticsEvent } from "@/enums/analytics";
import { SearchType } from "@/enums/search";
import {
  AGENT_SEARCH_PREFIX,
  PROMPT_DEAL_PREFIX,
  getSelectedDealPath,
} from "@/helpers/agents";
import {
  DEFAULT_MINIMIZED_HEIGHT,
  DEFAULT_MINIMIZED_WIDTH,
  VALID_URL_REGEX,
} from "@/helpers/constants";
import {
  AI_AGENTS_REGEX,
  NON_BREAKING_SPACE,
} from "@/helpers/constants/aiAgents";
import { DEFAULT_CMD_K_OPTIONS } from "@/helpers/constants/cmdK";
import { useEmitter } from "@/hooks/common";
import { useDealsBase } from "@/hooks/deals";
import { useNotification } from "@/hooks/notifications";
import { useLocalStorageSetting } from "@/hooks/options";
import { useResizable } from "@/hooks/resizable";
import { useAiAgentsStore } from "@/stores/ai/agents";
import { useFiles } from "@/stores/ai/files";
import { useAiSearchStore } from "@/stores/aiSearch";
import { useAnalyticsStore } from "@/stores/analytics";

import AiResults from "@/components/cmdK/AiResults.vue";
import CmdKHeader from "@/components/cmdK/CmdKHeader.vue";
import CommandInput from "@/components/cmdK/CommandInput.vue";
import IconUploadDoc from "@/components/icons/documents/IconUploadDoc.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { AiAgentBase, CreateAgentSessionPayload } from "@/models/aiAgents";
import type { ResizableEventValues } from "@/models/common";

const emit = defineEmits<{
  close: [];
}>();

const store = useAiSearchStore();
const aiAgentsStore = useAiAgentsStore();
const { t } = useI18n();
const { activeDeal } = useDealsBase();
const { trackEvent } = useAnalyticsStore();
const { dealResults } = storeToRefs(store);

const { agentSessionInfo } = storeToRefs(aiAgentsStore);
const cmdKOptions = useLocalStorageSetting(
  "cmdKOptions",
  DEFAULT_CMD_K_OPTIONS
);
const { escape } = useMagicKeys();
const draggable = useTemplateRef("draggable");
const aiFilesStore = useFiles();
const { selectedFiles, copiedFiles } = storeToRefs(aiFilesStore);
const {
  clearFiles,
  copySelectedFiles,
  clearCopiedFiles,
  handleFileUpload,
  allowedDocTypes,
} = aiFilesStore;

const { showMessage } = useNotification();

const emitter = useEmitter();

const { updatePosition } = useResizable(cmdKOptions, draggable);

const searchInputRef = useTemplateRef("searchInputRef");
const aiSearchPhrase = ref<string>("");
const activeType = ref<AiDialogType>(AiDialogType.Deals);
const searchType = ref<SearchType>(SearchType.AiDeal);
const previousStep = ref<AiDialogType | null>(null);
const dragSelector = ref<string>(".draggable");
const activeCmdKWidthAndHeight = ref({
  width: DEFAULT_CMD_K_OPTIONS.width,
  height: DEFAULT_CMD_K_OPTIONS.height,
  minWidth: 600,
  maxWidth: undefined,
  minHeight: 500,
  maxHeight: undefined,
  minimizedWidth: DEFAULT_MINIMIZED_WIDTH,
  minimizedHeight: DEFAULT_MINIMIZED_HEIGHT,
});
const dragInfo = ref({
  isDragging: false,
  left: 0,
  top: 0,
});

const fileDragInfo = ref({
  isDragOver: false,
  dragCounter: 0,
});

const closeModal = () => emit("close");

const closeDialog = () => {
  closeModal();
  activeType.value = AiDialogType.Deals;
};

const placeholder = computed(() =>
  searchType.value === SearchType.AiAgent
    ? t("AI_AGENTS.TYPE_A_COMMAND")
    : t("COMMON.SEARCH")
);

const showHeader = computed(() =>
  [AiDialogType.AgentChat, AiDialogType.SessionsHistory].includes(
    activeType.value
  )
);

// manual submit happens when user hits enter from the search bar without selecting a session
const manualSubmit = async () => {
  if (!agentSessionInfo.value.agent?.id) {
    return;
  }

  agentSessionInfo.value.prompt = aiSearchPhrase.value;
  await createAgentSession();
};

const handleClickOutside = () => {
  window.setTimeout(() => {
    if (
      !agentSessionInfo.value.isPinned &&
      !agentSessionInfo.value.hasActiveOverlay
    ) {
      closeDialog();
    }
  }, 0);
};

const handleDragEnd = (event: ResizableEventValues) => {
  updatePosition(event);

  /**
   * If the drag ended at the same position as the start,
   * it means that it was a click, not a drag, so we don't need
   * timeout because we need to allow the click action to happen
   * (redirect, add to the search bar etc.)
   */
  if (event.top === dragInfo.value.top && event.left === dragInfo.value.left) {
    dragInfo.value.isDragging = false;
    return;
  }

  setTimeout(() => {
    dragInfo.value.isDragging = false;
  }, 0);
};

const handleDragStart = (event: ResizableEventValues) => {
  emitter.emit("CMDK_DRAG_START");
  dragInfo.value.isDragging = true;
  dragInfo.value.left = event.left;
  dragInfo.value.top = event.top;
};

const handleAgentSelected = async (agent: AiAgentBase) => {
  if (dragInfo.value.isDragging) {
    return;
  }

  trackEvent(AnalyticsEvent.CmdKAgentTriggered, {
    agent_id: agent.id,
    agent_name: agent.name,
  });

  aiSearchPhrase.value = "";
  agentSessionInfo.value.isPromptingAgent = true;
  agentSessionInfo.value.agent = agent;

  searchInputRef.value?.insertItem("agent", agent.name, agent.id);

  await aiAgentsStore.getAllAgentSessions({
    agent_id: agent.id,
  });

  activeType.value = AiDialogType.Sessions;
};

const handleDealSelected = () => {
  if (dragInfo.value.isDragging) {
    return;
  }

  const dealId = agentSessionInfo.value.deal;

  if (!dealId) {
    return;
  }

  const deal = dealResults.value?.find((deal) => deal.id === dealId);

  if (!deal) {
    return;
  }

  searchInputRef.value?.insertItem(
    "deal",
    deal.application_name,
    getSelectedDealPath(dealId)
  );

  if (agentSessionInfo.value.agent) {
    activeType.value = AiDialogType.Sessions;
  }
};

const handleAgentPrompt = async () => {
  const text = aiSearchPhrase.value;

  // Remove URLs from consideration
  const textWithoutUrls = text.replace(
    new RegExp(VALID_URL_REGEX.source, "g"),
    ""
  );

  // Find the last "/" in the text (excluding URLs)
  const indexOfDealSearchChar = textWithoutUrls.lastIndexOf(PROMPT_DEAL_PREFIX);

  if (indexOfDealSearchChar === -1 || agentSessionInfo.value.requestIsLoading) {
    return;
  }

  agentSessionInfo.value.requestIsLoading = true;

  const dealSearchTextPrompt = text
    .substring(0, text.lastIndexOf(PROMPT_DEAL_PREFIX))
    .trim();
  const dealSearchDealPrompt = text
    .substring(text.lastIndexOf(PROMPT_DEAL_PREFIX) + 1)
    .trim();

  if (!dealSearchDealPrompt) {
    agentSessionInfo.value.requestIsLoading = false;
    return;
  }

  agentSessionInfo.value.prompt = dealSearchTextPrompt;

  await store.aiSearchDeals(dealSearchDealPrompt);

  activeType.value = AiDialogType.Deals;

  agentSessionInfo.value.requestIsLoading = false;
};

const handleNavigateBack = async () => {
  if (!previousStep.value) {
    activeType.value = AiDialogType.Deals;
    const isPinned = agentSessionInfo.value.isPinned;
    aiAgentsStore.clearAgentSessionInfo();
    agentSessionInfo.value.isPinned = isPinned;
    agentSessionInfo.value.isShown = true;
    return;
  }

  if (previousStep.value === AiDialogType.Deals) {
    activeType.value = AiDialogType.Deals;
    previousStep.value = null;
    return;
  }

  agentSessionInfo.value.requestIsLoading = true;

  if (previousStep.value === AiDialogType.SessionsHistory) {
    await aiAgentsStore.getAllAgentsSessions();
  } else if (previousStep.value === AiDialogType.Sessions) {
    await aiAgentsStore.getAllAgentSessions({
      agent_id: agentSessionInfo.value.agent!.id,
    });
  }

  agentSessionInfo.value.requestIsLoading = false;

  activeType.value = previousStep.value;
  previousStep.value = null;
};

const handleAgentRemove = () => {
  aiSearchPhrase.value = "";
  agentSessionInfo.value.isPromptingAgent = false;
  agentSessionInfo.value.agent = null;
  searchType.value = SearchType.AiDeal;
  activeType.value = AiDialogType.Deals;
};

const createAgentSession = async () => {
  const { agent, requestIsLoading, prompt } = agentSessionInfo.value;

  if (
    !agent?.id ||
    requestIsLoading ||
    (!prompt && !selectedFiles.value.length)
  ) {
    return;
  }

  agentSessionInfo.value.requestIsLoading = true;

  const payload: CreateAgentSessionPayload = {
    agent_id: agent.id,
  };

  const dealIdsFromPrompt = getDealsFromPrompt(prompt);

  const sessionId = await aiAgentsStore.createAgentSession(payload);

  if (!sessionId) {
    agentSessionInfo.value.requestIsLoading = false;
    return;
  }

  /**
   * if we have deals in prompt, use their ids
   * otherwise use active deal
   */

  const selectedDealPath = dealIdsFromPrompt.length
    ? ""
    : getSelectedDealPath(activeDeal.value?.id);

  const promptText = [agentSessionInfo.value.prompt, selectedDealPath].reduce(
    (acc, item) => {
      if (!item) {
        return acc;
      }

      const value = item.trim();
      return acc ? `${acc} ${value}` : value;
    },
    ""
  );

  try {
    await aiAgentsStore.promptAgent({
      prompt: promptText,
      files: selectedFiles.value,
      sessionId,
    });
    await aiAgentsStore.getAgentSession(sessionId.toString());
  } catch (error) {
    showMessage((error as Error).message, "error");
    agentSessionInfo.value.requestIsLoading = false;
    return;
  }

  activeType.value = AiDialogType.AgentChat;
  agentSessionInfo.value.requestIsLoading = false;
};

const getDealsFromPrompt = (prompt: string): string[] => {
  const matches = Array.from(
    prompt.matchAll(new RegExp(AI_AGENTS_REGEX.DEAL.source, "gi"))
  );
  return matches.map((match) => match[1]);
};

const handleSessionSelected = async (sessionId: string) => {
  if (dragInfo.value.isDragging) {
    return;
  }

  if (agentSessionInfo.value.requestIsLoading) {
    return;
  }

  agentSessionInfo.value.requestIsLoading = true;

  await aiAgentsStore.getAgentSession(sessionId);

  previousStep.value = activeType.value;
  activeType.value = AiDialogType.AgentChat;
  agentSessionInfo.value.requestIsLoading = false;
};

const updateAiSearchPhrase = (text: string) => {
  aiSearchPhrase.value = text;
};

const handleAgentHistory = async () => {
  if (dragInfo.value.isDragging) {
    return;
  }

  agentSessionInfo.value.requestIsLoading = true;

  await aiAgentsStore.getAllAgentsSessions();

  agentSessionInfo.value.requestIsLoading = false;
  previousStep.value = activeType.value;
  activeType.value = AiDialogType.SessionsHistory;
};

const isAgentSearch = (text: string) => {
  const agentSearchPrefixIndex = text.indexOf(AGENT_SEARCH_PREFIX);

  return (
    agentSearchPrefixIndex === 0 || text[agentSearchPrefixIndex - 1] === " "
  );
};

const handleAgentsSearch = async () => {
  const atIndex = aiSearchPhrase.value.lastIndexOf(AGENT_SEARCH_PREFIX);

  agentSessionInfo.value.abortControllers.searchDeals.abort();
  agentSessionInfo.value.abortControllers.searchDeals = new AbortController();

  await aiAgentsStore.getAll({
    search: aiSearchPhrase.value.slice(atIndex + 1),
    feature: AgentFeatureType.CommandBarAgentSearch,
  });

  activeType.value = AiDialogType.Agents;
};

const handleFileDragEnter = (e: DragEvent) => {
  e.preventDefault();
  e.stopPropagation();

  fileDragInfo.value.dragCounter++;

  if (e.dataTransfer?.types.includes("Files")) {
    fileDragInfo.value.isDragOver = true;
  }
};

const handleFileDragOver = (e: DragEvent) => {
  e.preventDefault();
  e.stopPropagation();

  if (e.dataTransfer) {
    e.dataTransfer.dropEffect = "copy";
  }
};

const handleFileDragLeave = (e: DragEvent) => {
  e.preventDefault();
  e.stopPropagation();

  fileDragInfo.value.dragCounter--;

  if (fileDragInfo.value.dragCounter <= 0) {
    fileDragInfo.value.isDragOver = false;
    fileDragInfo.value.dragCounter = 0;
  }
};

const handleFileDrop = (e: DragEvent) => {
  e.preventDefault();
  e.stopPropagation();

  fileDragInfo.value.isDragOver = false;
  fileDragInfo.value.dragCounter = 0;

  const files = e.dataTransfer?.files;
  if (files?.length) {
    handleFileUpload(files);
  }
};

/**
 * vue-resizable does not support cancel drag selectors
 * so we need to disable drag inside input fields
 */
const disableDragInsideInput = throttle((e: MouseEvent) => {
  const inputClasses = ["ql-editor", "command-input"];
  const isEditorField = inputClasses.some((className) => {
    const target = e.target as HTMLElement;

    if (!target) {
      return false;
    }

    return (
      target.classList?.contains(className) ||
      target.parentElement?.classList?.contains(className)
    );
  });
  dragSelector.value = isEditorField ? ".none-draggable" : ".draggable";
}, 100);

watchDebounced(
  aiSearchPhrase,
  async (newValue, oldValue) => {
    if (newValue === oldValue || newValue === "") {
      return;
    }

    if (
      newValue.indexOf(PROMPT_DEAL_PREFIX) === -1 &&
      oldValue.indexOf(PROMPT_DEAL_PREFIX) !== -1 &&
      !agentSessionInfo.value.requestIsLoading &&
      agentSessionInfo.value.isPromptingAgent &&
      agentSessionInfo.value.agent?.id
    ) {
      agentSessionInfo.value.requestIsLoading = true;

      await aiAgentsStore.getAllAgentSessions({
        agent_id: agentSessionInfo.value.agent.id,
      });

      agentSessionInfo.value.requestIsLoading = false;

      activeType.value = AiDialogType.Sessions;
    }

    if (agentSessionInfo.value.isPromptingAgent) {
      await handleAgentPrompt();
      previousStep.value = AiDialogType.Sessions;
      return;
    }

    if (newValue.includes(AGENT_SEARCH_PREFIX) && isAgentSearch(newValue)) {
      searchType.value = SearchType.AiAgent;
      await handleAgentsSearch();
      return;
    }

    /**
     * if it is a deal search without an agent, we don't want
     * to include other deals' urls in the search request
     */

    const isAgentDealSearch = agentSessionInfo.value.agent;

    const searchPhrase = isAgentDealSearch
      ? aiSearchPhrase.value
      : aiSearchPhrase.value.replace(
          new RegExp(VALID_URL_REGEX.source, "g"),
          ""
        );

    const searchPhraseWithoutNonBreakingSpace = searchPhrase
      .replace(new RegExp(NON_BREAKING_SPACE, "g"), " ")
      .trim();

    if (!searchPhraseWithoutNonBreakingSpace) {
      return;
    }

    const response = await store.aiSearchDeals(
      searchPhraseWithoutNonBreakingSpace,
      undefined,
      agentSessionInfo.value.abortControllers.searchDeals.signal
    );

    if (response) {
      trackEvent(AnalyticsEvent.CmdKSearchPerformed, {
        search_type: "deals",
        results_count: dealResults.value?.length ?? 0,
      });
      activeType.value = AiDialogType.Deals;
    }
  },
  { debounce: 600 }
);

watch(activeType, (updatedType, oldType) => {
  if (oldType && updatedType !== oldType) {
    trackEvent(AnalyticsEvent.CmdKModeChanged, {
      from_mode: oldType,
      to_mode: updatedType,
    });
  }
  if (updatedType === AiDialogType.AgentChat) {
    agentSessionInfo.value.htmlSearchContentCopy =
      agentSessionInfo.value.htmlSearchContent;
    agentSessionInfo.value.htmlSearchContent = "";
    copySelectedFiles();
    clearFiles();
  } else if (oldType === AiDialogType.AgentChat) {
    agentSessionInfo.value.htmlSearchContent =
      agentSessionInfo.value.htmlSearchContentCopy;
    selectedFiles.value = copiedFiles.value;
    clearCopiedFiles();
  }
});

onMounted(() => {
  trackEvent(AnalyticsEvent.CmdKOpened);
});

document.addEventListener("mouseover", disableDragInsideInput);

whenever(escape, () => {
  if (
    agentSessionInfo.value.isPinned ||
    agentSessionInfo.value.hasActiveOverlay
  ) {
    return;
  }
  emit("close");
});

onBeforeUnmount(() => {
  document.removeEventListener("mouseover", disableDragInsideInput);
  aiAgentsStore.clearAgentSessionInfo();
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

dialog {
  @apply w-[560px] rounded-md bg-white;
  &:focus-within {
    @apply outline-none;
  }
  &::backdrop {
    backdrop-filter: blur(0.5px);
  }
}
</style>
